/**
 * InfluencerDashboard — /influencer route.
 *
 * Marketing/creator hub for accounts with role='influencer' (or admin).
 * Shows:
 *   • Aggregate KPIs: signups, paid conversions, revenue attributed,
 *     conversion rate, active codes.
 *   • Codes table: per-code stats (uses, signups, conversions, revenue),
 *     discount %, active toggle, edit/delete.
 *   • Create-code modal with discount slider (0-100%) — 0% = tracking only.
 *   • Recent events feed.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Sparkles, Plus, Trash2, Copy, TrendingUp, Users, DollarSign, Percent, LineChart, Wallet,
} from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function InfluencerDashboard() {
  const { API, user } = useContext(AuthContext);
  const [stats, setStats]   = useState(null);
  const [codes, setCodes]   = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode]       = useState({ code: '', discount_pct: 0, notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        axios.get(`${API}/influencer/stats`),
        axios.get(`${API}/influencer/codes`),
        axios.get(`${API}/influencer/payouts`),
      ]);
      setStats(s.data);
      setCodes(c.data || []);
      setPayouts(p.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load your influencer data');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const createCode = async () => {
    if (!newCode.code || newCode.code.length < 3) { toast.error('Code needs at least 3 characters.'); return; }
    try {
      await axios.post(`${API}/influencer/codes`, {
        code:         newCode.code.trim().toUpperCase(),
        discount_pct: Number(newCode.discount_pct) || 0,
        notes:        newCode.notes,
        active:       true,
      });
      toast.success(`Code ${newCode.code.toUpperCase()} created`);
      setShowCreate(false);
      setNewCode({ code: '', discount_pct: 0, notes: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create code');
    }
  };

  const updateCode = async (id, patch) => {
    try {
      await axios.patch(`${API}/influencer/codes/${id}`, patch);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const deleteCode = async (id, code) => {
    if (!window.confirm(`Delete code ${code}? Existing attribution rows are kept for records.`)) return;
    try {
      await axios.delete(`${API}/influencer/codes/${id}`);
      toast.success('Code deleted');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/signup?ref=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(url);
    toast.success('Referral link copied');
  };

  const kpi = (icon, label, value, hint, testid) => (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
          {icon} {label}
        </div>
        <div className="font-heading text-3xl font-extrabold tracking-tight">{value}</div>
        {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
              <Sparkles className="w-3 h-3" /> Creator Program
            </div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">
              Welcome, {user?.name || user?.email?.split('@')[0] || 'Creator'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Mint discount codes, share them with your audience, and watch attribution + conversions roll in.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="influencer-new-code-button">
            <Plus className="w-4 h-4 mr-1" /> New code
          </Button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {kpi(<Sparkles className="w-3 h-3 text-primary" />, 'Active codes', stats?.active_codes ?? 0, `${stats?.total_codes || 0} total`, 'kpi-codes')}
          {kpi(<Users className="w-3 h-3 text-emerald-600" />, 'Signups', stats?.signups ?? 0, 'Attributed accounts', 'kpi-signups')}
          {kpi(<TrendingUp className="w-3 h-3 text-amber-600" />, 'Conversions', stats?.conversions ?? 0, 'Paid subscriptions', 'kpi-conversions')}
          {kpi(<Percent className="w-3 h-3 text-indigo-600" />, 'Conversion %', `${stats?.conversion_rate ?? 0}%`, 'Paid ÷ signups', 'kpi-rate')}
          {kpi(<DollarSign className="w-3 h-3 text-emerald-700" />, 'Revenue', money(stats?.revenue), 'Attributed net', 'kpi-revenue')}
        </div>

        {/* Codes table */}
        <Card className="mb-6">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-heading font-bold">Your codes</h2>
              <p className="text-xs text-muted-foreground">
                {codes.length} code{codes.length === 1 ? '' : 's'} · Share the referral link or tell your audience to enter the code at signup.
              </p>
            </div>
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : codes.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground mb-2">No codes yet.</p>
                <Button size="sm" onClick={() => setShowCreate(true)}>Create your first code</Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="influencer-codes-table">
                  <thead className="border-b border-border bg-slate-50 dark:bg-slate-900">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-4 py-2">Code</th>
                      <th className="px-2 py-2">Discount</th>
                      <th className="px-2 py-2 text-right">Signups</th>
                      <th className="px-2 py-2 text-right">Conversions</th>
                      <th className="px-2 py-2 text-right">Revenue</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((c) => (
                      <tr key={c.id} className="border-b border-border/60 hover:bg-slate-50/50 dark:hover:bg-slate-900/40" data-testid={`code-row-${c.code}`}>
                        <td className="px-4 py-2 font-mono font-bold">{c.code}</td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={c.discount_pct}
                            onChange={(e) => {
                              const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                              // Optimistic
                              setCodes((cs) => cs.map((x) => x.id === c.id ? { ...x, discount_pct: v } : x));
                            }}
                            onBlur={(e) => {
                              const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                              updateCode(c.id, { discount_pct: v });
                            }}
                            className="w-16 h-7 px-1.5 text-xs rounded border border-input bg-background"
                            data-testid={`code-discount-${c.code}`}
                          />
                          <span className="text-xs text-muted-foreground ml-1">%</span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono">{c.signups}</td>
                        <td className="px-2 py-2 text-right font-mono">{c.conversions}</td>
                        <td className="px-2 py-2 text-right font-mono">{money(c.revenue)}</td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            onClick={() => updateCode(c.id, { active: !c.active })}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                              c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                            }`}
                            data-testid={`code-toggle-${c.code}`}
                          >
                            {c.active ? 'ACTIVE' : 'PAUSED'}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => copyLink(c.code)} title="Copy referral link">
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteCode(c.id, c.code)} className="text-red-600" title="Delete code">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent events */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <LineChart className="w-4 h-4 text-primary" />
              <h2 className="font-heading font-bold">Recent activity</h2>
            </div>
            {(!stats?.recent_events || stats.recent_events.length === 0) ? (
              <p className="text-sm text-muted-foreground">
                No activity yet. Once someone signs up or subscribes with one of your codes, you&apos;ll see it here.
              </p>
            ) : (
              <div className="space-y-1.5" data-testid="influencer-recent-events">
                {stats.recent_events.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-slate-50 dark:hover:bg-slate-900 text-sm">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                        e.event_type === 'conversion' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {e.event_type === 'conversion' ? 'PAID' : 'SIGNUP'}
                    </span>
                    <span className="font-mono text-xs">{e.code}</span>
                    <span className="text-xs text-muted-foreground flex-1">
                      {new Date(e.created_at).toLocaleString()}
                    </span>
                    {e.order_amount != null && (
                      <span className="font-mono text-xs">{money(e.order_amount / 100)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payout ledger — what the platform owes this creator */}
        <Card className="mt-6" data-testid="payout-ledger">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-4 h-4 text-emerald-600" />
              <h2 className="font-heading font-bold">Payouts</h2>
              {payouts && (
                <span className="ml-auto text-xs text-muted-foreground">
                  Commission rate: <b>{payouts.commission_pct}%</b>
                </span>
              )}
            </div>
            {!payouts || payouts.periods.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No payouts yet. Once your codes drive paid subscriptions, monthly totals show up here.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="rounded-md border border-border p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total revenue</div>
                    <div className="font-heading text-xl font-extrabold">{money(payouts.totals.revenue)}</div>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Conversions</div>
                    <div className="font-heading text-xl font-extrabold">{payouts.totals.conversions}</div>
                  </div>
                  <div className="rounded-md border-2 border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-950/30 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Owed to you</div>
                    <div className="font-heading text-xl font-extrabold text-emerald-700" data-testid="payout-total-owed">
                      {money(payouts.totals.payout)}
                    </div>
                  </div>
                </div>
                <table className="w-full text-sm" data-testid="payout-ledger-table">
                  <thead className="border-b border-border">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-2 py-1.5">Period</th>
                      <th className="px-2 py-1.5 text-right">Conversions</th>
                      <th className="px-2 py-1.5 text-right">Attributed revenue</th>
                      <th className="px-2 py-1.5 text-right">Payout ({payouts.commission_pct}%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.periods.map((p) => (
                      <tr key={p.period} className="border-b border-border/40" data-testid={`payout-row-${p.period}`}>
                        <td className="px-2 py-1.5 font-mono">{p.period}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{p.conversions}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{money(p.revenue)}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-700">{money(p.payout)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-muted-foreground mt-3">
                  Payouts are computed on attributed net revenue at your commission rate. Contact an admin to adjust the rate.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="influencer-create-modal">
          <DialogHeader>
            <DialogTitle>New influencer code</DialogTitle>
            <DialogDescription>
              Codes are uppercase & unique. Set discount to 0% for pure tracking (no discount, still attributes).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code</Label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                placeholder="e.g. YOURNAME20"
                data-testid="influencer-new-code-input"
              />
            </div>
            <div>
              <Label>Discount (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={newCode.discount_pct}
                onChange={(e) => setNewCode({ ...newCode, discount_pct: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) })}
                data-testid="influencer-new-code-discount"
              />
              <p className="text-[11px] text-muted-foreground mt-1">0% = tracking only, no discount at checkout.</p>
            </div>
            <div>
              <Label>Notes (private)</Label>
              <Textarea
                rows={2}
                value={newCode.notes}
                onChange={(e) => setNewCode({ ...newCode, notes: e.target.value })}
                placeholder="e.g. YouTube video launch, Q1 podcast tour…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createCode} data-testid="influencer-new-code-save">Create code</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
